import React, { useState, useEffect, useRef } from 'react';
import { Segment, Header, Icon, Form, Button, Message, Input, Label, Divider } from 'semantic-ui-react';
import SignedImage from './SignedImage';
import { toKebab } from './FileStagingList';
import "../../styles/editLiveBookForm.css";
/**
 * EditLiveBookForm — lightweight in-place edit of a live book's metadata.
 * Title · Cover · Author name+photo. No PDF, no re-import. Overrides the Books list like
 * the Add form. Cover and author photo are click-to-replace boxes (current shown).
 *
 * Props:
 *   book        — the live book row ({ uuid, bookTitle, authorName, coverUrl, authorImageS3Path })
 *   onSubmit    — ({ bookTitle, coverFile, coverAlias, authorName, newAuthorFile }) => Promise
 *   onCancel    — return to the list
 *   saving      — boolean
 *   userId      — for signing preview images
 */
const FIELD_W = 400;
const BOX = 120;

// A small current-image box that swaps to a freshly-picked file (click to replace).
const ImagePicker = ({ currentS3Path, userId, file, onPick, onClear, label, fallbackIcon, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current && inputRef.current.click()}
        title="Click to choose a new image"
        style={{
          width: BOX, height: BOX, borderRadius: 8, overflow: 'hidden', position: 'relative',
          cursor: disabled ? 'default' : 'pointer', border: '2px dashed #ccc', background: '#fafafa',
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="new" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <SignedImage s3Path={currentS3Path} userId={userId} width={`${BOX}px`} height={`${BOX}px`} fallbackIcon={fallbackIcon} />
        )}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)',
          color: '#fff', fontSize: '0.68em', textAlign: 'center', padding: '2px 0',
        }}>
          {previewUrl ? 'New · click to change' : label}
        </div>
      </div>
      {previewUrl && (
        <Button basic size="mini" content="Revert" icon="undo" style={{ marginTop: '0.4em' }}
          disabled={disabled} onClick={onClear} />
      )}
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onPick(f); e.target.value = ''; }} />
    </div>
  );
};

// ACX wants a SQUARE cover at high resolution, and it is a different asset from the print
// cover — a book is portrait because a book is portrait, and squaring the print art either
// crops the top and bottom away or letterboxes dead space. So this is its own slot.
//
// The numbers are ACX's published floor at time of writing; verify against their current spec
// before a submission, since it does move. Warning rather than blocking is deliberate: a BC
// mid-way through preparing art should be able to save a placeholder without a gate, and a
// spec that hard-fails on a number we got wrong is worse than one that flags it.
const ACX_MIN_PX = 2400;

const AcxCoverPicker = ({ currentS3Path, userId, file, onPick, onClear, disabled }) => {
  const [dims, setDims] = useState(null);
  const inputRef = useRef(null);

  // Measure on pick. naturalWidth/Height are the real pixels, not the styled box.
  useEffect(() => {
    if (!file) { setDims(null); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setDims(null);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const notSquare = dims && dims.w !== dims.h;
  const tooSmall = dims && Math.min(dims.w, dims.h) < ACX_MIN_PX;

  return (
    <div>
      <div style={{ display: 'flex', gap: '1em', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <ImagePicker
          currentS3Path={currentS3Path} userId={userId}
          file={file} onPick={onPick} onClear={onClear}
          label="Click to choose" fallbackIcon="images outline" disabled={disabled}
        />
        <div style={{ flex: '1 1 240px', minWidth: 240 }}>
          {dims && (
            <div style={{ fontSize: '0.85em', marginBottom: '0.4em' }}>
              {dims.w} × {dims.h} px
            </div>
          )}
          {notSquare && (
            <Message size="mini" warning style={{ padding: '0.6em 0.8em' }}>
              <Icon name="warning sign" />
              Not square. ACX requires a square cover — this is {dims.w} × {dims.h}.
            </Message>
          )}
          {tooSmall && (
            <Message size="mini" warning style={{ padding: '0.6em 0.8em' }}>
              <Icon name="warning sign" />
              Below {ACX_MIN_PX}px on its shortest side. It is used full-bleed behind the
              order page, not just as a thumbnail.
            </Message>
          )}
          {dims && !notSquare && !tooSmall && (
            <Message size="mini" positive style={{ padding: '0.6em 0.8em' }}>
              <Icon name="check" /> Square and {dims.w}px — meets the ACX floor.
            </Message>
          )}
        </div>
      </div>
    </div>
  );
};

const EditLiveBookForm = ({ book, onSubmit, onCancel, saving = false, userId }) => {
  const [bookTitle, setBookTitle] = useState(book?.bookTitle || '');
  const [authorName, setAuthorName] = useState(book?.authorName || '');
  const [coverFile, setCoverFile] = useState(null);
  const [newAuthorFile, setNewAuthorFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ----- audiobook credits -----------------------------------------------
  // Filled in LATE by design: the narrator cannot be known until every role is cast, human
  // voice actors included. This form does no re-import, which is what makes that possible.
  const [showCredits, setShowCredits] = useState(false);
  const [castCredit, setCastCredit] = useState(book?.castCredit || '');
  const [narratorLeads, setNarratorLeads] = useState(
    Array.isArray(book?.narratorLeads) ? book.narratorLeads.join(', ') : (book?.narratorLeads || '')
  );
  const [rightsHolder, setRightsHolder] = useState(book?.rightsHolder || '');
  const [copyrightYear, setCopyrightYear] = useState(book?.copyrightYear || '');
  const [openingCreditsText, setOpeningCreditsText] = useState(book?.openingCreditsText || '');
  const [closingCreditsText, setClosingCreditsText] = useState(book?.closingCreditsText || '');
  const [audioCoverFile, setAudioCoverFile] = useState(null);
  const busy = saving || submitting;

  const kebab = bookTitle ? toKebab(bookTitle) : (book?.uuid || 'cover');
  const coverAlias = coverFile ? `${kebab}.${(coverFile.name.split('.').pop() || 'jpg')}` : null;

  const complete = !!bookTitle.trim();

  const handleSubmit = async () => {
    if (!complete) return;
    setSubmitting(true);
    try {
      await onSubmit({
        bookTitle: bookTitle.trim(),
        coverFile,
        coverAlias,
        authorName: authorName.trim() || null,
        newAuthorFile,
        // Square ACX cover — a sibling of the print cover, not a replacement. Named apart
        // from it so the two never overwrite each other in the same folder.
        audioCoverFile,
        audioCoverAlias: audioCoverFile
          ? `${kebab}-audiobook.${(audioCoverFile.name.split('.').pop() || 'jpg')}`
          : null,
        // Sent as a group so the thunk can spread them. Empty strings are meaningful — they
        // CLEAR a stored value, which is how a BC discards custom wording and returns to the
        // generated credit.
        credits: {
          castCredit: castCredit.trim(),
          narratorLeads: narratorLeads.trim()
            ? JSON.stringify(narratorLeads.split(',').map((x) => x.trim()).filter(Boolean))
            : '',
          rightsHolder: rightsHolder.trim(),
          copyrightYear: copyrightYear.trim(),
          openingCreditsText: openingCreditsText.trim(),
          closingCreditsText: closingCreditsText.trim(),
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Segment>
      <Header as="h3">
        <Icon name="edit" />
        <Header.Content>
          Edit “{book?.bookTitle}”
          <Header.Subheader>Metadata only — title, cover, author. The book isn't re-imported.</Header.Subheader>
        </Header.Content>
      </Header>

      <Form>
        <div className='edit-form-section'>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
            <label style={{ minWidth: 80, textAlign: 'right', fontWeight: 'bold', margin: 0 }}>Book Title</label>
            <Input value={bookTitle} onChange={(e) => setBookTitle(e.target.value)} disabled={busy} style={{ width: FIELD_W }} />
          </div>

          <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', marginTop: '0.9em', flexWrap: 'wrap' }}>
            <div>
              <Header as="h4" style={{ marginBottom: '0.6em' }}>
                <Label circular color="purple" size="small">C</Label> Cover
              </Header>
              <ImagePicker
                currentS3Path={book?.coverUrl} userId={userId}
                file={coverFile} onPick={setCoverFile} onClear={() => setCoverFile(null)}
                label="Click to change" fallbackIcon="book" disabled={busy}
              />
            </div>
          </div>
        </div>

        <div className='edit-form-section'>
          <Header as="h4" style={{ marginBottom: '0.6em', marginTop: '1.5em' }}>
            <Label circular color="blue" size="small">A</Label> Author
          </Header>
          <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <ImagePicker
                currentS3Path={book?.authorImageS3Path} userId={userId}
                file={newAuthorFile} onPick={setNewAuthorFile} onClear={() => setNewAuthorFile(null)}
                label="Click to change" fallbackIcon="user circle" disabled={busy}
              />
            </div>
            <div style={{ flex: '1 1 260px', marginLeft: '-40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
                <label style={{ minWidth: 80, textAlign: 'right', fontWeight: 'bold', margin: 0 }}>Author</label>
                <Input value={authorName} onChange={(e) => setAuthorName(e.target.value)} disabled={busy} style={{ width: FIELD_W }} />
              </div>
            </div>
          </div>
        </div>
        {/* AUDIOBOOK CREDITS — collapsed by default. Most edits here are a title or a cover;
            credits are a once-per-book act, and unfolding them every time would bury the
            common case. */}
        <div className='edit-form-section edit-form-section-bar ' >
          <div
            onClick={() => !busy && setShowCredits((v) => !v)}
            style={{ cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5em' }}
          >
            <Icon name={showCredits ? 'caret down' : 'caret right'} />
            <strong>Audiobook Credits</strong>
            <span style={{ color: '#888', fontSize: '0.9em', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              spoken at the start and end — generated from the title and author unless you write your own
            </span>
          </div>

          {showCredits && (
            <div style={{ marginTop: '0.9em' }}>
              <Form.Field>
                <label className="edit-form-input-label">Cast credit</label>
                <Input
                  className='edit-form-credit-entry'
                  value={castCredit} onChange={(e) => setCastCredit(e.target.value)} disabled={busy}
                  placeholder="a full cast of Actors and Authorator Players"
                />
                <div className='edit-form-hint'>
                  Follows the words “narrated by”, so start it lower-case. Leave empty until the
                  cast is settled — the credit simply omits the narrator line until then.
                </div>
              </Form.Field>

              <Form.Field >
                <label className="edit-form-input-label">Named leads</label>
                <Input
                  className='edit-form-credit-entry'
                  value={narratorLeads} onChange={(e) => setNarratorLeads(e.target.value)} disabled={busy}
                  placeholder="Marisol Vane, Tobias Crane"
                />
                <div className='edit-form-hint'>
                  Comma-separated. Read before the cast credit: “narrated by Marisol Vane and
                  Tobias Crane, with a full cast of…”.
                </div>
              </Form.Field>

              {/* Holder on the same left edge as everything above; the year carries to the
                  right edge, where a short field reads as deliberate rather than truncated. */}
              <div style={{
                display: 'flex', gap: '1em', marginTop: '0.8em', flexWrap: 'wrap',
                justifyContent: 'space-between', alignItems: 'flex-end',
              }}>
                <Form.Field className='edit-form-credit-entry'>
                  <label className="edit-form-input-label">Rights holder</label>
                  <Input className='edit-form-credit-entry' value={rightsHolder} onChange={(e) => setRightsHolder(e.target.value)}
                    disabled={busy} placeholder="defaults to the author" style={{ width: '100%' }} />
                  <div className='edit-form-hint'>
                    Name of the person, company or agency holding the rights this copyrighted book.
                  </div>
                </Form.Field>
                <Form.Field style={{ margin: 0, textAlign: 'right' }}>
                  <label className="edit-form-input-label">Copyright year</label>
                  <Input className='copyright' value={copyrightYear} onChange={(e) => setCopyrightYear(e.target.value)}
                    disabled={busy} placeholder="e.g. 2002" />
                  <div className='edit-form-hint' style={{ height: '2.25rem' }}>
                    Most recent copyright year.
                  </div>
                </Form.Field>
              </div>

              <Form.Field style={{ marginTop: '1.2em' }}>
                <Header className="edit-form-input-label" as="h4" style={{ marginBottom: '0.6em' }}>
                  <Label circular color="blue" size="small">AB</Label> Audiobook cover (square, {ACX_MIN_PX}px+)
                </Header>
                <AcxCoverPicker
                  currentS3Path={book?.audioCoverUrl} userId={userId}
                  file={audioCoverFile} onPick={setAudioCoverFile}
                  onClear={() => setAudioCoverFile(null)} disabled={busy}
                />
                <div className='edit-form-hint' style={{ width: '100%' }}>
                  Separate from the print cover, which stays portrait. Used both as a thumbnail
                  and as a full-bleed background, so the detail has to survive at both sizes.
                </div>
              </Form.Field>

              <Divider
                horizontal
                style={{
                  fontSize: '0.8em', color: '#999', marginTop: '1.4em',
                  // Semantic centres a horizontal divider's label. Everything else in this
                  // panel starts at the left edge, so centring it alone broke the column.
                  justifyContent: 'flex-start', textAlign: 'left',
                }}
              >
                or write them yourself
              </Divider>

              <Form.TextArea
                className='edit-form-text-area-left-align-label'
                label="Opening credits (overrides the generated text)"
                value={openingCreditsText} onChange={(e) => setOpeningCreditsText(e.target.value)}
                disabled={busy} rows={2}
                placeholder="Leave empty to use: “The Ghost of Madonna 13. Written by E.T. Ellison. Narrated by…”"
              />
              <Form.TextArea
                className='edit-form-text-area-left-align-label'
                label="Closing credits (overrides the generated text)"
                value={closingCreditsText} onChange={(e) => setClosingCreditsText(e.target.value)}
                disabled={busy} rows={2}
                placeholder="Leave empty to use: “This has been The Ghost of Madonna 13, written by…”"
              />
              <div className='edit-form-hint' style={{ width: '100%', marginTop: '0.4em' }}>
                Written exactly as typed. Clear a box to go back to the generated wording.
              </div>
            </div>
          )}
        </div>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5em', marginTop: '1em' }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button primary onClick={handleSubmit} disabled={!complete || busy} loading={busy}>
          <Icon name="save" /> Save Changes
        </Button>
      </div>

      {!complete && (
        <Message size="mini" warning style={{ marginTop: '0.5em' }}>
          <Icon name="warning sign" /> A title is required.
        </Message>
      )}
    </Segment>
  );
};

export default EditLiveBookForm;
